import React, { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import FunctionsIcon from "@mui/icons-material/Functions";
import ConstructionIcon from "@mui/icons-material/Construction";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { secondAxios } from "../../../../api/axios";
import paths from "../../../../api/paths.api";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import DescriptionIcon from "@mui/icons-material/Description";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";

export default function PlantsTable() {
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [currentPlant, setCurrentPlant] = useState({
    name: "",
    clientName: "",
    sharepointLink: "",
    people: [],
  });

  // Load users for people selection
  const fetchUsers = async () => {
    try {
      const res = await secondAxios.get("/user");
      setAllUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Load plants
  const fetchPlants = async () => {
    try {
      setState({ isLoading: true, isError: false });
      const res = await secondAxios.get("/plant");
      console.log("Fetched plants:", res.data);

      // Filter plants by client name
      const clientName = process.env.REACT_APP_CLIENT_NAME;
      const filteredPlants = Array.isArray(res.data)
        ? res.data.filter((plant) => plant.clientName === clientName)
        : [];

      setPlants(res.data);
    } catch (err) {
      console.error("Error fetching plants:", err);
      setState({ isLoading: false, isError: true });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchPlants();
    fetchUsers();
  }, []);

  // Open modal for creating new plant
  const handleOpenCreateModal = () => {
    setModalMode("create");
    setCurrentPlant({
      name: "",
      clientName: process.env.REACT_APP_CLIENT_NAME || "",
      sharepointLink: "",
      people: [],
    });
    setModalOpen(true);
  };

  // Open modal for editing existing plant
  const handleOpenEditModal = (plant) => {
    setModalMode("edit");
    // Extract user IDs from people array if it exists
    const peopleIds = plant.people
      ? plant.people.map((user) => (typeof user === "string" ? user : user.id))
      : [];
    setCurrentPlant({
      ...plant,
      people: peopleIds,
    });
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentPlant({
      name: "",
      clientName: "",
      sharepointLink: "",
      people: [],
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setCurrentPlant((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle people selection change
  const handlePeopleChange = (selectedUsers) => {
    const userIds = selectedUsers.map((user) =>
      typeof user === "string" ? user : user.id,
    );
    setCurrentPlant((prev) => ({
      ...prev,
      people: userIds,
    }));
  };

  // Save plant (create or update)
  const handleSavePlant = async () => {
    try {
      // Prepare payload according to CreatePlantDto
      const payload = {
        name: currentPlant.name,
        clientName: currentPlant.clientName || undefined,
        sharepointLink: currentPlant.sharepointLink || undefined,
        people:
          currentPlant.people && currentPlant.people.length > 0
            ? currentPlant.people
            : undefined,
      };

      if (modalMode === "create") {
        const res = await secondAxios.post("/plant", payload);
        // Refresh plants list to get filtered results
        await fetchPlants();
      } else {
        await secondAxios.put(`/plant/${currentPlant.id}`, payload);
        // Refresh plants list to get updated data
        await fetchPlants();
      }
      handleCloseModal();
    } catch (err) {
      console.error(
        `Error ${modalMode === "create" ? "creating" : "updating"} plant:`,
        err,
      );
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Delete plant
  const handleDeletePlant = async (plantId) => {
    if (!window.confirm("Are you sure you want to delete this plant?")) {
      return;
    }

    try {
      await secondAxios.delete(`/plant/${plantId}`);
      // Refresh plants list to get filtered results
      await fetchPlants();
    } catch (err) {
      console.error("Error deleting plant:", err);
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Columns definition
  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 50 },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "clientName", size: 40, header: "Client", size: 40 },
      {
        accessorKey: "tasks",
        header: "Action Items",
        size: 40,
        enableEditing: false,
        enableColumnActions: false,
        Edit: () => null,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/task`);
              }}
            >
              <AssignmentIcon color="primary" />
            </IconButton>
          );
        },
      },
      // {
      //   accessorKey: "ga",
      //   header: "GA",
      //   Cell: ({ cell }) => {
      //     return (
      //       // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
      //       <Box
      //         display="flex"
      //         alignItems="center"
      //         gap={1}
      //         noNavigate={true}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           // console.log(
      //           //   "mcs clicked",
      //           //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
      //           //   cell.row.original
      //           // );
      //           navigate(
      //             `/projects/${
      //               cell.row.original.id
      //             }/equipments/main_tank#main-nav=${"ga"}&mcs-tab=Spec`
      //           );
      //         }}
      //       >
      //         <CategoryIcon />
      //       </Box>
      //     );
      //   },
      // },
      {
        accessorKey: "estimation",
        header: "Estimation",
        size: 40,
        Cell: ({ cell }) => {
          return (
            // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                // console.log(
                //   "mcs clicked",
                //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                //   cell.row.original
                // );
                navigate(`/plant/${cell.row.original.id}/estimation`);
              }}
            >
              <FunctionsIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "dcs",
        header: "DCS",
        size: 40,
        Cell: ({ cell }) => {
          return (
            // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                // console.log(
                //   "mcs clicked",
                //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                //   cell.row.original
                // );
                navigate(`/plant/${cell.row.original.id}/dcs`);
              }}
            >
              <DocumentScannerIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "mcs",
        header: "MCS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        Cell: ({ cell, row }) => {
          // console.log("row", row.original);
          // const users = row.original?.users || [];

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                // console.log("mcs clicked");
                navigate(`/plant/${cell.row.original.id}/mcs`);
                // navigate(
                //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                // );
              }}
              // disabled={true}
            >
              <ManageAccountsIcon color="primary" />
            </IconButton>
          );
        },
      },

      {
        accessorKey: "pcs",
        header: "PCS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/pcs`);
              }}
            >
              <ConstructionIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "cms",
        header: "CMS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/cms`);
              }}
            >
              <ChangeCircleIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "pms",
        header: "PMS",
        size: 40,
        enableEditing: false,
        enableColumnActions: false,
        Edit: () => null,
        Cell: ({ row, cell }) => {
          return (
            // <a
            //   href={`/projects/${row.original.id}/equipments/main_tank#main-nav=PMS&pms-tab=4D`}
            // >
            <CalendarTodayIcon
              onClick={(e) => {
                console.log("pms clicked");
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/pms`);
              }}
              color="primary"
            />
            // </a>
          );
        },
      },

      {
        accessorKey: "mis",
        header: "MIS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        Cell: ({ cell, row }) => {
          // console.log("row", row.original);
          // const users = row.original?.users || [];

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                // console.log("mcs clicked");
                navigate(`/plant/${cell.row.original.id}/mis`);
                // navigate(
                //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                // );
              }}
              // disabled={true}
            >
              <DescriptionIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "people",
        header: "People",
        Cell: ({ cell }) => {
          const people = cell.getValue();
          if (!people || !Array.isArray(people) || people.length === 0) {
            return <span>-</span>;
          }
          return (
            <span>
              {people
                .map((user) =>
                  typeof user === "object" ? user.name || user.email : user,
                )
                .join(", ")}
            </span>
          );
        },
      },
      {
        accessorKey: "projects",
        header: "No. sub projects",
        Cell: ({ cell, row }) => {
          const plant = row.original;
          const projects = plant.projects || plant.project;

          if (!projects) {
            return <span>0</span>;
          }

          // Handle different data structures
          const projectsArray = Array.isArray(projects)
            ? projects
            : projects
              ? [projects]
              : [];

          // Filter by client name if needed
          const clientName = process.env.REACT_APP_CLIENT_NAME;
          const filteredProjects = projectsArray.filter(
            (p) => p && p.clientName === clientName,
          );

          return <span>{filteredProjects.length}</span>;
        },
      },
    ],
    [],
  );

  // Top toolbar actions
  const renderTopToolbarCustomActions = () => (
    <Box sx={{ display: "flex", gap: 1, p: 1 }}>
      <Button
        color="primary"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenCreateModal}
      >
        Add Plant
      </Button>
    </Box>
  );

  return (
    <>
      <MaterialReactTable
        data={plants}
        columns={columns}
        enableRowActions
        positionActionsColumn="last"
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            const target = event.target;
            if (
              !target.closest(".MuiButtonBase-root") &&
              !target.closest("a")
            ) {
              // console.log("id", paths.plant, row.original);
              // navigate(paths.plant + "/" + row.original.id);
            }
          },
          sx: { cursor: "pointer" },
        })}
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(row.original);
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePlant(row.original.id);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
        muiToolbarAlertBannerProps={
          state.isError
            ? { color: "error", children: "Error loading plant data" }
            : undefined
        }
        state={{
          isLoading: state.isLoading,
          showAlertBanner: state.isError,
        }}
      />

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "create" ? "Create New Plant" : "Edit Plant"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <TextField
              label="Plant Name"
              value={currentPlant.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Client Name"
              value={currentPlant.clientName}
              onChange={(e) => handleInputChange("clientName", e.target.value)}
              fullWidth
              helperText={
                modalMode === "create"
                  ? "Default: " + process.env.REACT_APP_CLIENT_NAME
                  : ""
              }
            />
            <TextField
              label="SharePoint Link"
              value={currentPlant.sharepointLink}
              onChange={(e) =>
                handleInputChange("sharepointLink", e.target.value)
              }
              fullWidth
              type="url"
              helperText="Optional SharePoint URL"
            />
            <Autocomplete
              multiple
              options={allUsers}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? allUsers.find((u) => u.id === option)?.name ||
                    allUsers.find((u) => u.id === option)?.email ||
                    option
                  : option.name || option.email || ""
              }
              isOptionEqualToValue={(option, value) => {
                const optionId =
                  typeof option === "string" ? option : option.id;
                const valueId = typeof value === "string" ? value : value.id;
                return optionId === valueId;
              }}
              value={
                currentPlant.people && allUsers.length > 0
                  ? currentPlant.people
                      .map((id) => allUsers.find((u) => u.id === id))
                      .filter((user) => user !== undefined)
                  : []
              }
              onChange={(event, newValue) => {
                handlePeopleChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="People"
                  helperText="Select users to associate with this plant"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label =
                    typeof option === "string"
                      ? allUsers.find((u) => u.id === option)?.name ||
                        allUsers.find((u) => u.id === option)?.email ||
                        option
                      : option.name || option.email || "";
                  return (
                    <Chip
                      variant="outlined"
                      label={label}
                      {...getTagProps({ index })}
                      key={typeof option === "string" ? option : option.id}
                    />
                  );
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSavePlant} variant="contained" color="primary">
            {modalMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
